import React from 'react';
import { Link } from 'react-router-dom';
import { helpLinkTarget, withCurrentSelection } from '../../utils/appLinks';
import { useCanOpen } from '../../context/NavAccessContext';
import { Info, Lightbulb, TriangleAlert } from 'lucide-react';
import { articleHref } from './helpCenterApi';

/*
 * Help articles are plain data (azfApi/helpCenter/content, served by
 * GET /api/helpCenter) so writers never touch JSX.
 * Inline syntax inside any string:
 *   **bold**            emphasis
 *   [[Save]]            an on-screen label (button, tab, menu item)
 *   `code`              a literal value the user types
 *   [text](/path)       link inside the app
 *   [text](help:slug)   link to another article in the same topic
 *   [text](help:topic/slug)
 *   [text](https://…)   external link (new tab)
 */
const INLINE = /(\*\*[^*]+\*\*|\[\[[^\]]+\]\]|\[[^\]]+\]\([^)\s]+\)|`[^`]+`)/g;
const LINK = /^\[([^\]]+)\]\(([^)\s]+)\)$/;

function linkFor(href, topicSlug) {
  if (href.startsWith('help:')) {
    const ref = href.slice(5);
    const [topic, slug] = ref.includes('/') ? ref.split('/') : [topicSlug, ref];
    return { to: articleHref(topic, slug) };
  }
  if (href.startsWith('/')) return { to: withCurrentSelection(href) };
  return { external: href };
}

export function Inline({ text, topicSlug }) {
  const canOpen = useCanOpen();
  if (text == null) return null;
  const parts = String(text).split(INLINE);
  return parts.map((part, i) => {
    if (!part) return null;
    if (part.startsWith('**') && part.endsWith('**')) {
      // Bold text can hold UI labels and code: "**No [[Lodgeic AI]] in the menu:**".
      return <strong key={i}><Inline text={part.slice(2, -2)} topicSlug={topicSlug} /></strong>;
    }
    if (part.startsWith('[[') && part.endsWith(']]')) {
      return <span key={i} className="hc-ui">{part.slice(2, -2)}</span>;
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return <code key={i} className="hc-code">{part.slice(1, -1)}</code>;
    }
    const link = part.match(LINK);
    if (link) {
      const target = linkFor(link[2], topicSlug);
      if (target.external) {
        return <a key={i} className="hc-link" href={target.external} target="_blank" rel="noreferrer">{link[1]}</a>;
      }
      const go = helpLinkTarget(target.to, canOpen);
      return <Link key={i} className="hc-link" to={go.to} state={go.state}>{link[1]}</Link>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

export const slugify = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const CALLOUTS = {
  tip: { Icon: Lightbulb, label: 'Tip' },
  note: { Icon: Info, label: 'Good to know' },
  warn: { Icon: TriangleAlert, label: 'Careful' },
};

function Block({ block, topicSlug }) {
  const t = (text) => <Inline text={text} topicSlug={topicSlug} />;

  if (block.p) return <p>{t(block.p)}</p>;
  if (block.h) return <h3 id={slugify(block.h)}>{block.h}</h3>;
  if (block.steps) {
    return (
      <ol className="hc-steps">
        {block.steps.map((s, i) => <li key={i}><span>{t(s)}</span></li>)}
      </ol>
    );
  }
  if (block.list) {
    return <ul className="hc-list">{block.list.map((s, i) => <li key={i}>{t(s)}</li>)}</ul>;
  }
  if (block.dl) {
    return (
      <dl className="hc-dl">
        {block.dl.map(([term, def]) => (
          <div key={term}>
            <dt>{t(term)}</dt>
            <dd>{t(def)}</dd>
          </div>
        ))}
      </dl>
    );
  }
  if (block.table) {
    return (
      <div className="hc-table-wrap">
        <table className="hc-table">
          <thead><tr>{block.table.head.map((h) => <th key={h}>{t(h)}</th>)}</tr></thead>
          <tbody>
            {block.table.rows.map((row, r) => (
              <tr key={r}>{row.map((cell, c) => <td key={c}>{t(cell)}</td>)}</tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  const kind = Object.keys(CALLOUTS).find((k) => block[k]);
  if (kind) {
    const { Icon, label } = CALLOUTS[kind];
    return (
      <aside className={`hc-callout hc-callout-${kind}`}>
        <Icon size={16} aria-hidden="true" />
        <div><strong>{label}.</strong> {t(block[kind])}</div>
      </aside>
    );
  }
  return null;
}

export default function HelpBody({ blocks, topicSlug }) {
  return (
    <div className="hc-prose">
      {(blocks || []).map((block, i) => <Block key={i} block={block} topicSlug={topicSlug} />)}
    </div>
  );
}
